// src/app/api/showcaseApi.ts
//
// Online showcases: the pitch video a child hands in on a showcase week is
// scored by the panel (tutors, staff, admins), finalised by an admin with
// feedback, an optional award and a certificate, and, with the parent's
// consent, premiered in the academy gallery (signed-in families only).
//
// Family:  /learn/:childId/showcase…, /learn/:childId/gallery…
// Panel:   /showcase-panel/…

import { baseApi } from "./baseApi";

interface Ok<T> {
  success: boolean;
  message?: string;
  data: T;
}

export type ShowcaseStatus = "awaiting_panel" | "finalised";

/* ─── Family ─────────────────────────────────────────────────────── */

export interface FamilyShowcaseEntry {
  id: string;
  weekKey: string;
  weekTitle: string | null;
  showcaseName: string;
  lessonTitle: string | null;
  microLessonKey: string;
  submittedAt: string | null;
  status: ShowcaseStatus;
  criteria: string[];
  result: {
    average: number | null;
    criterionAverages: (number | null)[];
    award: string | null;
    feedback: string | null;
    finalisedAt: string;
    seen: boolean;
    panelSize: number;
  } | null;
  certificate: { name: string; track: string; date: string } | null;
  projectTitle: string | null;
  gallery: { consent: boolean; approved: boolean; premiereAt: string | null; live: boolean };
}

export interface FamilyShowcase {
  child: { id: string; firstName: string; track: string };
  entries: FamilyShowcaseEntry[];
}

export interface GalleryItem {
  id: string;
  firstName: string;
  city: string | null;
  track: string;
  ageGroup: string;
  weekTitle: string | null;
  showcaseName: string;
  projectTitle: string | null;
  award: string | null;
  premiereAt: string;
  isOwn: boolean;
}

/* ─── Panel ──────────────────────────────────────────────────────── */

export type PanelView = "to_score" | "to_finalise" | "finalised" | "gallery";

export interface PanelEntry {
  id: string;
  child: { id: string; firstName: string; lastInitial: string; age: number | null } | null;
  track: string;
  weekKey: string;
  weekTitle: string | null;
  showcaseName: string;
  lessonTitle: string | null;
  microLessonKey: string;
  attemptNumber: number;
  submittedAt: string | null;
  reflection: { built: string | null; helps: string | null; learned: string | null };
  criteria: string[];
  status: ShowcaseStatus;
  panelCount: number;
  myScore: { scores: number[]; comment: string | null; at: string } | null;
  panel: { judgeName: string | null; scores: number[]; comment: string | null; at: string }[] | null;
  average: number | null;
  criterionAverages: (number | null)[] | null;
  result: { award: string | null; feedback: string | null; finalisedAt: string } | null;
  projectTitle: string | null;
  gallery: { consent: boolean; approvedAt: string | null; premiereAt: string | null; live: boolean };
  canFinalise: boolean;
  videoUrl?: string | null;
}

export const SCORE_LABELS: Record<number, string> = {
  1: "Not yet",
  2: "Getting there",
  3: "Good",
  4: "Strong",
  5: "Outstanding",
};

/** Award ideas per age group; the panel can write its own. */
export const AWARD_IDEAS: Record<string, string[]> = {
  foundation_builders: ["Star Storyteller", "Creative Spark", "Brave Presenter", "Super Game Designer", "Young Inventor"],
  explorers: ["Best Opening", "Clearest Customer", "Technical Star", "Design Award", "Innovation Award", "Most Improved"],
  innovators: ["Best Technical Explanation", "Product Thinker", "Most Polished Build", "Calm Under Questions", "Most Improved"],
};

export const showcaseApi = baseApi
  .enhanceEndpoints({ addTagTypes: ["Showcase", "Gallery", "ShowcasePanel"] })
  .injectEndpoints({
    endpoints: (b) => ({
      // ── Family ───────────────────────────────────────────────────
      getShowcase: b.query<Ok<FamilyShowcase>, string>({
        query: (childId) => `/learn/${childId}/showcase`,
        providesTags: ["Showcase"],
      }),
      markShowcaseSeen: b.mutation<Ok<null>, { childId: string; entryId: string }>({
        query: ({ childId, entryId }) => ({
          url: `/learn/${childId}/showcase/${entryId}/seen`,
          method: "POST",
        }),
        invalidatesTags: ["Showcase"],
      }),
      setGalleryConsent: b.mutation<
        Ok<FamilyShowcaseEntry>,
        { childId: string; entryId: string; consent: boolean; projectTitle?: string }
      >({
        query: ({ childId, entryId, ...body }) => ({
          url: `/learn/${childId}/showcase/${entryId}/gallery`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["Showcase", "Gallery"],
      }),
      getShowcaseVideoUrl: b.mutation<Ok<{ url: string }>, { childId: string; entryId: string }>({
        query: ({ childId, entryId }) => ({
          url: `/learn/${childId}/showcase/${entryId}/video-url`,
          method: "GET",
        }),
      }),
      getGallery: b.query<Ok<GalleryItem[]>, { childId: string; track?: string }>({
        query: ({ childId, track }) =>
          `/learn/${childId}/gallery${track ? `?track=${encodeURIComponent(track)}` : ""}`,
        providesTags: ["Gallery"],
      }),
      getGalleryVideoUrl: b.mutation<Ok<{ url: string }>, { childId: string; entryId: string }>({
        query: ({ childId, entryId }) => ({
          url: `/learn/${childId}/gallery/${entryId}/video-url`,
          method: "GET",
        }),
      }),

      // ── Panel ────────────────────────────────────────────────────
      getPanelEntries: b.query<
        Ok<PanelEntry[]> & { counts: Record<PanelView, number> },
        { view: PanelView; track?: string }
      >({
        query: ({ view, track }) =>
          `/showcase-panel/entries?view=${view}${track ? `&track=${encodeURIComponent(track)}` : ""}`,
        providesTags: ["ShowcasePanel"],
      }),
      getPanelEntry: b.query<Ok<PanelEntry>, string>({
        query: (id) => `/showcase-panel/entries/${id}`,
        providesTags: ["ShowcasePanel"],
      }),
      scoreShowcase: b.mutation<Ok<PanelEntry>, { id: string; scores: number[]; comment: string }>({
        query: ({ id, ...body }) => ({ url: `/showcase-panel/entries/${id}/score`, method: "POST", body }),
        invalidatesTags: ["ShowcasePanel"],
      }),
      finaliseShowcase: b.mutation<Ok<PanelEntry>, { id: string; feedback: string; award?: string }>({
        query: ({ id, ...body }) => ({ url: `/showcase-panel/entries/${id}/finalise`, method: "POST", body }),
        invalidatesTags: ["ShowcasePanel"],
      }),
      setGalleryApproval: b.mutation<Ok<PanelEntry>, { id: string; approve: boolean; premiereAt?: string }>({
        query: ({ id, ...body }) => ({ url: `/showcase-panel/entries/${id}/gallery`, method: "POST", body }),
        invalidatesTags: ["ShowcasePanel"],
      }),
    }),
  });

export const {
  useGetShowcaseQuery,
  useMarkShowcaseSeenMutation,
  useSetGalleryConsentMutation,
  useGetShowcaseVideoUrlMutation,
  useGetGalleryQuery,
  useGetGalleryVideoUrlMutation,
  useGetPanelEntriesQuery,
  useGetPanelEntryQuery,
  useScoreShowcaseMutation,
  useFinaliseShowcaseMutation,
  useSetGalleryApprovalMutation,
} = showcaseApi;
